import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button, Table, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Trash2, Pencil } from "lucide-react";

const userPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
const canCreate = userPermissions.includes("create_Category");
const canEdit = userPermissions.includes("update_Category");
const canDelete = userPermissions.includes("delete_Category");
const API_BASE_URL = process.env.REACT_APP_API_URL;
export default function CategoryList() {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('token');

  const axiosConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${token}`
    }
  }), [token]);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/categories`, axiosConfig);
      setCategories(res.data);
    } catch (err) {
      console.error("Error loading categories", err);
    } finally {
      setLoading(false);
    }
  }, [axiosConfig]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleDelete = async (id) => {
    if (!canDelete) return alert("Permission not allowed");

    if (window.confirm('Delete this category?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/categories/${id}`, axiosConfig);
        loadCategories();
      } catch (err) {
        console.error("Delete error", err);
      }
    }
  };

  const toggleStatus = async (category) => {
    if (!canEdit) return alert("Permission not allowed");

    try {
      await axios.put(
        `${API_BASE_URL}/api/categories/${category._id}`,
        { ...category, status: !category.status },
        axiosConfig
      );
      loadCategories();
    } catch (err) {
      console.error("Toggle status error", err);
    }
  };

  const handleEditClick = (e, id) => {
    if (!canEdit) {
      e.preventDefault();
      alert("Permission not allowed");
      return;
    }
    window.location.href = `/edit-category/${id}`;
  };

  return (
    <div className="container mt-4">
      <style>{`
        .heading {
          color: #102234;
        }
        .add-button {
          background-color: #102234;
          border-color: #102234;
          color: white;
          padding: 6px 16px;
          font-size: 14px;
          border-radius: 5px;
          width: 145px;
        }
        .form-check-input:checked {
          background-color: green !important;
          border-color: green !important;
        }
        .form-check-input:not(:checked) {
          background-color: red !important;
          border-color: red !important;
        }
        .btn-disabled {
          cursor: not-allowed !important;
          opacity: 0.6;
          pointer-events: auto;
        }
        @media (max-width: 768px) {
          .table-responsive {
            overflow-x: auto;
          }
        }
      `}</style>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
        <h4 className="heading mb-2 mb-md-0">Category</h4>
        <Button
          href={canCreate ? "/add-category" : "#"}
          className={`add-button ${!canCreate ? 'btn-disabled' : ''}`}
          onClick={(e) => {
            if (!canCreate) {
              e.preventDefault();
              alert("Permission not allowed");
            }
          }}
          title={canCreate ? "Add Category" : "No permission to add"}
        >
          + Add Category
        </Button>
      </div>

      <Form.Control
        type="text"
        placeholder="🔍 Search by Category Name"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="mb-3"
      />

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover>
            <thead className="table-dark">
              <tr>
                <th>No.</th>
                <th>Name</th>
                <th>Route</th>
                <th>Description</th>
                <th>Remarks</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {categories
                .filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
                .map((cat, i) => (
                  <tr key={cat._id}>
                    <td>{i + 1}</td>
                    <td>{cat.name}</td>
                    <td>{cat.route}</td>
                    <td>{cat.description || '-'}</td>
                    <td>{cat.remarks || '-'}</td>
                    <td>
                      <OverlayTrigger
                        overlay={
                          <Tooltip>
                            {canEdit ? "Toggle status" : "No permission to update"}
                          </Tooltip>
                        }
                      >
                        <span>
                          <Form.Check
                            type="switch"
                            checked={cat.status}
                            onChange={() => toggleStatus(cat)}
                            disabled={!canEdit}
                          />
                        </span>
                      </OverlayTrigger>
                    </td>
                    <td>
                      <OverlayTrigger
                        overlay={<Tooltip>{canEdit ? "Edit" : "No permission to edit"}</Tooltip>}
                      >
                        <button
                          className={`btn btn-sm btn-outline-primary me-2 ${!canEdit ? 'btn-disabled' : ''}`}
                          onClick={(e) => handleEditClick(e, cat._id)}
                        >
                          <Pencil size={16} />
                        </button>
                      </OverlayTrigger>

                      <OverlayTrigger
                        overlay={<Tooltip>{canDelete ? "Delete" : "No permission to delete"}</Tooltip>}
                      >
                        <button
                          className={`btn btn-sm btn-outline-danger ${!canDelete ? 'btn-disabled' : ''}`}
                          onClick={() => handleDelete(cat._id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </OverlayTrigger>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}
